import { OneTariff, PriceData, TOUTariffPrice } from '@/type/stationType'
import { TARIFF_TYPE, tariffTypeLists } from '@/utils/const'
import timeToNumber from '@/utils/timeToNumber'
import { CloseCircleFilled, PlusOutlined } from '@ant-design/icons'
import { Input, Select, Tabs, TabsProps } from 'antd'
import { nanoid } from 'nanoid'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface TariffSetProps {
  value: PriceData[]
  onChange: (value: PriceData[]) => void
  suffix: string
}

const TariffSet: React.FC<TariffSetProps> = ({ value, onChange, suffix }) => {
  const { t } = useTranslation()

  const timeList = useRef<{ label: string; value: string }[]>([])
  useEffect(() => {
    timeList.current = []
    for (let i = 0; i < 48; i++) {
      const val =
        Math.floor(i / 2)
          .toString()
          .padStart(2, '0') +
        ':' +
        ((i % 2) * 30).toString().padStart(2, '0')
      timeList.current.push({ label: val, value: val })
    }
    timeList.current.push({ label: '23:59', value: '23:59' })
  }, [])

  const handleClickAdd = () => {
    onChange([...value, { startTime: '', endTime: '', value: '', key: nanoid() }])
  }

  const handleEndTimeChange = (val: string, index: number) => {
    const newValue = value
    newValue[index].endTime = val
    onChange(newValue)
  }

  const handlePriceChange = (val: string, index: number) => {
    const newValue = value
    newValue[index].value = val
    onChange(newValue)
  }

  const handleDelete = (index: number) => {
    const newValue = value
    newValue.splice(index, 1)
    onChange(newValue)
  }

  return (
    <div className='tariff-set'>
      {value.map((item, index) => (
        <div className='one-price-data' key={item.key}>
          <div className='delete-btn' onClick={() => handleDelete(index)}>
            <CloseCircleFilled />
          </div>
          <div className='time-place'>
            <div className='time-form'>
              <div>{t('site.startTime')}</div>
              <Select value={item.startTime} disabled />
            </div>
            <div className='time-form'>
              <div>{t('site.endTime')}</div>
              <Select
                value={item.endTime}
                options={timeList.current.map((time) => ({
                  label: time.label,
                  value: time.value,
                  disabled: timeToNumber(time.value) <= timeToNumber(item.startTime),
                }))}
                onChange={(val) => handleEndTimeChange(val, index)}
              />
            </div>
          </div>
          <div className='price-place'>
            <div>{t('site.price')}</div>
            <Input
              value={item.value}
              suffix={suffix + '/kWh'}
              onChange={(e) => handlePriceChange(e.target.value, index)}
            />
          </div>
        </div>
      ))}
      <div className='one-price-data add-btn' onClick={handleClickAdd}>
        <PlusOutlined />
      </div>
    </div>
  )
}

interface TariffProps {
  value?: OneTariff
  onChange?: (value: OneTariff) => void
  id?: string
  suffix: string
}

const TariffItem: React.FC<TariffProps> = ({ value, onChange, id, suffix }) => {
  const { t } = useTranslation()

  const handleChangeTouPrice = (val: PriceData[], tab: 'daily' | 'weekend' | 'weekday') => {
    val.forEach((item, index) => {
      if (index === 0) {
        item.startTime = '00:00'
      } else {
        item.startTime = val[index - 1].endTime
      }
    })
    const newPrice = value?.price as TOUTariffPrice[]
    const target = newPrice.find((item) => item.tab === tab)
    if (target) {
      target.data = val
    }
    onChange?.({ type: value?.type || TARIFF_TYPE.FIXED, price: newPrice })
  }

  const touTabsItem: TabsProps['items'] = [
    {
      key: '1',
      label: t('site.daily'),
      children: (
        <TariffSet
          value={(value?.price as TOUTariffPrice[])[0]?.data || []}
          onChange={(val) => handleChangeTouPrice(val, 'daily')}
          suffix={suffix}
        />
      ),
    },
  ]

  const touWTabsItem: TabsProps['items'] = [
    {
      key: '1',
      label: t('site.weekday'),
      children: (
        <TariffSet
          value={(value?.price as TOUTariffPrice[])[0]?.data || []}
          onChange={(val) => handleChangeTouPrice(val, 'weekday')}
          suffix={suffix}
        />
      ),
    },
    {
      key: '2',
      label: t('site.weekend'),
      children: (
        <TariffSet
          value={(value?.price as TOUTariffPrice[])[1]?.data || []}
          onChange={(val) => handleChangeTouPrice(val, 'weekend')}
          suffix={suffix}
        />
      ),
    },
  ]

  const handleChangeType = (val: TARIFF_TYPE) => {
    let price: string | TOUTariffPrice[] = ''
    if (val === TARIFF_TYPE.FIXED) {
      price = '0.00'
    } else if (val === TARIFF_TYPE.TERM_OF_USE) {
      price = [{ tab: 'daily', data: [] }]
    } else if (val === TARIFF_TYPE.TERM_OF_USE_WEEKDAY_WEEKEND) {
      price = [
        { tab: 'weekday', data: [] },
        { tab: 'weekend', data: [] },
      ]
    }
    onChange?.({ type: val, price })
  }

  const handleChangePrice = (val: string) => {
    onChange?.({ type: value?.type || TARIFF_TYPE.FIXED, price: val })
  }

  return (
    <div className='tariff-item' id={id}>
      <div className='item-line'>
        <Select
          options={tariffTypeLists.map((item) => ({ label: t(item.label), value: item.value }))}
          value={value?.type}
          onChange={handleChangeType}
        />
        {value?.type === TARIFF_TYPE.FIXED && (
          <Input
            value={value.price as string}
            onChange={(e) => handleChangePrice(e.target.value)}
            suffix={suffix + '/kWh'}
          />
        )}
      </div>
      {value?.type === TARIFF_TYPE.TERM_OF_USE ? (
        <Tabs defaultActiveKey='1' items={touTabsItem} />
      ) : value?.type === TARIFF_TYPE.TERM_OF_USE_WEEKDAY_WEEKEND ? (
        <Tabs defaultActiveKey='1' items={touWTabsItem} />
      ) : null}
    </div>
  )
}

export default TariffItem
